import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Download as DownloadIcon } from '@mui/icons-material';
import { calendarAPI, CalendarEvent } from '../api/calendar.api';
import { squadAPI } from '../api/squad.api';
import { useNavigate } from 'react-router-dom';

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [squads, setSquads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSquadId, setSelectedSquadId] = useState<string>('all');
  const [selectedEventType, setSelectedEventType] = useState<'all' | 'session' | 'competition'>('all');
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);

  useEffect(() => {
    fetchSquads();
    fetchEvents();
  }, [selectedSquadId, selectedEventType]);

  const fetchSquads = async () => {
    try {
      const response = await squadAPI.getSquads({ limit: 100 });
      setSquads(response.squads || []);
    } catch (err) {
      console.error('Error fetching squads:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await calendarAPI.getCalendarEvents({
        squadId: selectedSquadId !== 'all' ? selectedSquadId : undefined,
        type: selectedEventType,
      });
      setEvents(response.events || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = (info: any) => {
    const event = events.find((e) => e.id === info.event.id);
    if (event) {
      setSelectedEvent(event);
      setEventDialogOpen(true);
    }
  };

  const handleEventView = () => {
    if (selectedEvent) {
      if (selectedEvent.type === 'session') {
        navigate(`/sessions/${selectedEvent.id}`);
      } else if (selectedEvent.type === 'competition') {
        navigate(`/competitions/${selectedEvent.id}`);
      }
      setEventDialogOpen(false);
    }
  };

  const handleExportCalendar = async () => {
    try {
      const blob = await calendarAPI.exportCalendar(
        selectedSquadId !== 'all' ? selectedSquadId : undefined
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'gkm-calendar.ics';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting calendar:', err);
    }
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'session':
        return 'primary';
      case 'competition':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Calendar
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportCalendar}
        >
          Export Calendar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Squad</InputLabel>
            <Select
              value={selectedSquadId}
              label="Squad"
              onChange={(e) => setSelectedSquadId(e.target.value)}
            >
              <MenuItem value="all">All Squads</MenuItem>
              {squads.map((squad) => (
                <MenuItem key={squad.id} value={squad.id}>
                  {squad.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ButtonGroup variant="outlined">
            <Button
              variant={selectedEventType === 'all' ? 'contained' : 'outlined'}
              onClick={() => setSelectedEventType('all')}
            >
              All
            </Button>
            <Button
              variant={selectedEventType === 'session' ? 'contained' : 'outlined'}
              onClick={() => setSelectedEventType('session')}
            >
              Sessions
            </Button>
            <Button
              variant={selectedEventType === 'competition' ? 'contained' : 'outlined'}
              onClick={() => setSelectedEventType('competition')}
            >
              Competitions
            </Button>
          </ButtonGroup>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            events={events}
            eventClick={handleEventClick}
            height="auto"
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }}
          />
        )}
      </Paper>

      {/* Event Detail Dialog */}
      <Dialog
        open={eventDialogOpen}
        onClose={() => setEventDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedEvent?.title}
          <Chip
            label={selectedEvent?.type}
            color={getEventTypeColor(selectedEvent?.type || '')}
            size="small"
            sx={{ ml: 1 }}
          />
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Start:</strong> {new Date(selectedEvent.start).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>End:</strong> {new Date(selectedEvent.end).toLocaleString()}
              </Typography>
              {selectedEvent.location && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Location:</strong> {selectedEvent.location}
                </Typography>
              )}
              {selectedEvent.squad && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Squad:</strong> {selectedEvent.squad.name}
                </Typography>
              )}
              {selectedEvent.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {selectedEvent.description}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDialogOpen(false)}>Close</Button>
          <Button onClick={handleEventView} variant="contained">
            View Details
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarPage;
